/* ==========================================================================
   Cart Drawer controller (Honeylove-style copy, Gotyu colors)
   - Keeps <cart-drawer> / <cart-drawer-items> API for Section Rendering.
   - Adds: scrolling announcement, gift-with-purchase auto sync, upsell quick-add.
   Depends on: cart.js (CartItems), global.js (trapFocus, fetchConfig), window.routes
   ========================================================================== */

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay) overlay.addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();

    // Remove an applied discount code via its chip's "×" (delegated on the
    // persistent <cart-drawer>, so it survives inner re-renders).
    this.addEventListener('click', (evt) => {
      const rm = evt.target.closest('[data-cd-remove-code]');
      if (!rm) return;
      evt.preventDefault();
      this.removeDiscountCode(rm.getAttribute('data-cd-remove-code'));
    });

    // Shipping-protection toggle + accordion (delegated on the persistent
    // <cart-drawer>, so it survives inner re-renders — see syncShippingProtection).
    this.addEventListener('click', (evt) => {
      const toggle = evt.target.closest('[data-cd-sp-toggle]');
      if (toggle) {
        evt.preventDefault();
        const block = toggle.closest('[data-cd-sp]');
        if (block) this.toggleShippingProtection(block);
        return;
      }
      const acc = evt.target.closest('[data-cd-sp-accordion]');
      if (acc) {
        evt.preventDefault();
        const block = acc.closest('[data-cd-sp]');
        const panel = block && block.querySelector('[data-cd-sp-details]');
        if (panel) panel.classList.toggle('is-open');
        acc.classList.toggle('is-open');
      }
    });

    // Expose a global sync hook so cart.js (quantity changes) can trigger the reward-code check.
    window.cartDrawerSync = () => {
      const drawer = document.querySelector('cart-drawer');
      if (!drawer) return;
      if (typeof drawer.syncDiscounts === 'function') drawer.syncDiscounts();
      if (typeof drawer.syncGift === 'function') drawer.syncGift();
      if (typeof drawer.syncShippingProtection === 'function') drawer.syncShippingProtection();
      // cart.js swaps .drawer__inner via innerHTML, so any <script> in the upsell
      // size-guide markup is inert — re-run it here.
      if (typeof drawer.runUpsellScripts === 'function') drawer.runUpsellScripts();
      // The reductions row + code chips are JS-populated; cart.js re-renders them
      // as hidden, so repopulate them here or they vanish on a quantity change.
      if (typeof drawer.refreshReductions === 'function') drawer.refreshReductions();
      if (typeof drawer.renderDiscountMessage === 'function') drawer.renderDiscountMessage();
    };

    this.bindFooterActions();
    this.syncDiscounts();
    this.syncGift();
    this.syncShippingProtection();
    this.refreshReductions();
    this.syncFooterHeight();
  }

  /* The upsell cards embed the product-page size-guide snippets (ss-modal-size-v2
     + size-chart-*), whose behaviour lives in inline <script> tags. Those never
     execute when the drawer is re-rendered via innerHTML, so we re-run them once
     each render. The scripts are product-id-scoped and idempotent (function
     declarations / `.onclick =` / alerts), so re-running is safe. guidedetaille
     is NOT re-run here — its script is omitted in the drawer and handled by the
     delegated listener below. */
  runUpsellScripts() {
    const scope = this.querySelector('.cd-upsell');
    if (!scope) return;
    scope.querySelectorAll('script').forEach((old) => {
      if (old.dataset.cdRan) return;
      const fresh = document.createElement('script');
      for (const attr of old.attributes) fresh.setAttribute(attr.name, attr.value);
      fresh.textContent = old.textContent;
      fresh.dataset.cdRan = '1';
      old.replaceWith(fresh);
    });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    setTimeout(() => {
      this.classList.add('animate', 'active');
      this.syncFooterHeight();
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.cd-empty') || this.querySelector('.drawer__inner')
          : document.getElementById('CartDrawer');
        const focusElement =
          this.querySelector('.cd-close') || this.querySelector('.drawer__inner');
        if (typeof trapFocus === 'function' && containerToTrapFocusOn) {
          trapFocus(containerToTrapFocusOn, focusElement);
        }
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active', 'cd-guide-open');
    if (typeof removeTrapFocus === 'function') removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  /* -------------------------------------------------- Section Rendering ---- */
  renderContents(parsedState, open = true) {
    // `is-empty` lives on <cart-drawer> (this), not on .drawer__inner. Keep it in
    // sync so open() traps focus on the right container once items are added.
    if (typeof parsedState.item_count === 'number') {
      this.classList.toggle('is-empty', parsedState.item_count === 0);
    }
    this.productId = parsedState.id;

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (!sectionElement || !parsedState.sections[section.id]) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    // Re-bind overlay every render (the element is replaced by innerHTML swap).
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay) overlay.addEventListener('click', this.close.bind(this));

    this.bindFooterActions();
    this.syncDiscounts();
    this.syncGift();
    this.runUpsellScripts();
    this.renderDiscountMessage();
    this.refreshReductions();
    this.syncShippingProtection();
    this.syncFooterHeight();

    if (open) {
      setTimeout(() => this.open());
    }
  }

  clear() {
    fetch(
      `${routes.cart_clear_url}/?sections=${this.getSectionsToRender()
        .map((section) => section.id)
        .join(',')}&sections_url=${window.location.pathname}`,
      { ...fetchConfig() }
    )
      .then((response) => response.json())
      .then((parsedState) => {
        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartFooter = document.getElementById('main-cart-footer');
        if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
        this.classList.toggle('is-empty', parsedState.item_count === 0);
        this.renderContents(parsedState, false);
      })
      .catch((e) => console.error(e));
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      { id: 'cart-drawer', selector: '#CartDrawer' },
      { id: 'cart-icon-bubble' },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  /* ------------------------------------------------------ Footer height ----
     Sur mobile le .cd-footer est en position:absolute au-dessus du scroll
     (.cd-body). Sa hauteur varie (protection livraison, code promo, trust row…),
     donc on la mesure et on la publie en --cd-footer-h : .cd-body s'en sert
     comme padding-bottom, sinon les upsells restent cachés sous le footer. */
  syncFooterHeight() {
    const inner = this.querySelector('.drawer__inner');
    const footer = this.querySelector('.cd-footer');
    if (!inner) return;

    if (!footer) {
      inner.style.removeProperty('--cd-footer-h');
      return;
    }

    const apply = () => {
      const h = Math.round(footer.getBoundingClientRect().height);
      if (h > 0) inner.style.setProperty('--cd-footer-h', `${h}px`);
    };
    apply();

    // Le footer est re-créé à chaque re-render : on ré-observe l'élément courant.
    if (this._footerRO) this._footerRO.disconnect();
    if (typeof ResizeObserver === 'function') {
      this._footerRO = new ResizeObserver(apply);
      this._footerRO.observe(footer);
    }

    if (!this._footerResizeBound) {
      this._footerResizeBound = true;
      window.addEventListener('resize', () => this.syncFooterHeight());
    }
  }

  /* ------------------------------------------------ Footer interactions ---- */
  bindFooterActions() {
    // "Show details" toggle
    const toggle = this.querySelector('[data-cd-toggle-breakdown]');
    const breakdown = this.querySelector('.cd-breakdown');
    if (toggle && breakdown && !toggle.dataset.bound) {
      toggle.dataset.bound = 'true';
      toggle.addEventListener('click', () => {
        const open = breakdown.hasAttribute('hidden');
        if (open) breakdown.removeAttribute('hidden');
        else breakdown.setAttribute('hidden', '');
        toggle.setAttribute('aria-expanded', String(open));
      });
    }

    // Discount code apply — inline (AJAX), no redirect to the cart page.
    const applyBtn = this.querySelector('[data-cd-apply-discount]');
    if (applyBtn && !applyBtn.dataset.bound) {
      applyBtn.dataset.bound = 'true';
      applyBtn.addEventListener('click', () => this.applyDiscountCode());
    }
    const discountInput = this.querySelector('.cd-discount__input');
    if (discountInput && !discountInput.dataset.bound) {
      discountInput.dataset.bound = 'true';
      discountInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          this.applyDiscountCode();
        }
      });
    }
  }

  /* Apply a customer-typed discount code without leaving the drawer.
     Merges it with the codes already on the cart, applies via /cart/update.js
     (same mechanism as the tier codes), re-renders, and reports whether Shopify
     accepted it (applicable). */
  applyDiscountCode() {
    if (this._applyingDiscount) return;
    const input = this.querySelector('.cd-discount__input');
    const btn = this.querySelector('[data-cd-apply-discount]');
    const code = input && input.value.trim();
    if (!code) return;

    const updateUrl = (typeof routes !== 'undefined' && routes.cart_update_url) || `${this.cartRoot}cart/update.js`;

    this._applyingDiscount = true;
    if (btn) btn.disabled = true;

    fetch(`${this.cartRoot}cart.js`, { cache: 'no-store' })
      .then((r) => r.json())
      .then((cart) => {
        const applied = (cart.discount_codes || [])
          .filter((d) => d.applicable !== false)
          .map((d) => d.code);
        if (!applied.some((c) => c.toLowerCase() === code.toLowerCase())) applied.push(code);

        return fetch(updateUrl, {
          ...fetchConfig(),
          body: JSON.stringify({
            discount: applied.join(','),
            sections: this.getSectionsToRender().map((s) => s.id).join(','),
            sections_url: window.location.pathname,
          }),
        }).then((r) => r.json());
      })
      .then((state) => {
        const codes = (state && state.discount_codes) || [];
        const match = codes.find((d) => (d.code || '').toLowerCase() === code.toLowerCase());
        const ok = !!(match && match.applicable !== false);

        if (state && state.sections) this.renderContents(state, false);
        this.updateDiscountDisplay(state);

        const freshInput = this.querySelector('.cd-discount__input');
        if (ok) {
          // Success is already shown by the applied-code chip → no message.
          if (freshInput) freshInput.value = '';
          this._discountMsg = null;
          clearTimeout(this._discountMsgTimer);
          this.renderDiscountMessage();
        } else {
          this.showDiscountMessage(false);
        }
      })
      .catch((e) => {
        console.error('cart-drawer discount apply', e);
        this.showDiscountMessage(false);
      })
      .finally(() => {
        this._applyingDiscount = false;
        const freshBtn = this.querySelector('[data-cd-apply-discount]');
        if (freshBtn) freshBtn.disabled = false;
      });
  }

  /* Feedback message for the discount field. Stored on the instance and kept
     visible for DISCOUNT_MSG_MS, re-applied after every re-render (renderContents
     calls renderDiscountMessage) so a follow-up cart sync can't wipe it early.
     Strings come from window.cartStrings (localized in window-variables.liquid). */
  showDiscountMessage(ok) {
    const strings = window.cartStrings || {};
    const text = ok
      ? (strings.discountApplied || 'Discount code applied')
      : (strings.discountError || 'Invalid discount code');
    const DISCOUNT_MSG_MS = 6000;
    this._discountMsg = { text, ok, until: Date.now() + DISCOUNT_MSG_MS };
    this.renderDiscountMessage();
    clearTimeout(this._discountMsgTimer);
    this._discountMsgTimer = setTimeout(() => {
      this._discountMsg = null;
      this.renderDiscountMessage();
    }, DISCOUNT_MSG_MS);
  }

  renderDiscountMessage() {
    const msg = this.querySelector('.cd-discount__msg');
    if (!msg) return;
    const m = this._discountMsg;
    if (m && Date.now() < m.until) {
      msg.textContent = m.text;
      msg.hidden = false;
      msg.classList.toggle('is-error', !m.ok);
      msg.classList.toggle('is-ok', m.ok);
    } else {
      msg.hidden = true;
      msg.textContent = '';
      msg.classList.remove('is-error', 'is-ok');
    }
  }

  /* ------------------------------------------------ Reductions summary ----- */
  /* Refreshes the two savings rows from a fresh cart.js read:
       1. "Économies site" [data-cd-savings]  = démarque produit uniquement
          (compare_total from Liquid − cart.original_total_price). Hidden when 0.
       2. "Discount"       [data-cd-reductions] = code(s) promo : puces + montant
          (cart.total_discounts). Affichée UNIQUEMENT si un code promo est appliqué.
     compare_total vient d'un data-attr Liquid (stable : les codes ne changent
     pas le compare_at) ; tout le reste vient du JSON. */
  refreshReductions() {
    const box = this.querySelector('[data-cd-reductions]');
    const savingsBox = this.querySelector('[data-cd-savings]');
    if (!box && !savingsBox) return;
    fetch(`${this.cartRoot}cart.js`, { cache: 'no-store' })
      .then((r) => r.json())
      .then((cart) => this.renderReductions(cart, box))
      .catch(() => {});
  }

  renderReductions(cart, box) {
    cart = cart || {};

    /* --- 1) Ligne "Économies site" : démarque produit, hors code promo --- */
    const savingsBox = this.querySelector('[data-cd-savings]');
    if (savingsBox) {
      const compareTotal = parseInt(savingsBox.dataset.compareTotal, 10) || 0;
      const originalTotal = typeof cart.original_total_price === 'number'
        ? cart.original_total_price
        : (parseInt(savingsBox.dataset.originalTotal, 10) || compareTotal);
      const websiteSavings = Math.max(0, compareTotal - originalTotal);
      const savingsAmountEl = savingsBox.querySelector('.cd-savings__amount');
      savingsBox.hidden = websiteSavings <= 0;
      if (savingsAmountEl) {
        savingsAmountEl.textContent = websiteSavings > 0 ? '-' + formatMoney(websiteSavings) : '';
      }
    }

    /* --- 2) Ligne "Discount" : code(s) promo (visible seulement si code appliqué) --- */
    box = box || this.querySelector('[data-cd-reductions]');
    if (!box) return;
    const codesWrap = box.querySelector('[data-cd-applied-codes]');
    const amountEl = box.querySelector('.cd-reductions__amount');

    const progress = this.querySelector('.cd-progress');
    const managed = [];
    if (progress) {
      [progress.dataset.tier2Code, progress.dataset.tier3Code].forEach((c) => {
        c = (c || '').trim().toLowerCase();
        if (c) managed.push(c);
      });
    }

    const codes = ((cart && cart.discount_codes) || [])
      .filter((d) => d.applicable !== false)
      .map((d) => d.code)
      .filter(Boolean);

    // Visibilité de la ligne réductions :
    //  - mode fusionné (data-cd-combined) : visible s'il y a une économie OU un code ;
    //  - mode séparé : la ligne Discount ne s'affiche QUE si un code promo est appliqué.
    const isCombined = box.hasAttribute('data-cd-combined');
    let show;
    if (isCombined) {
      const compareTotal = parseInt(box.dataset.compareTotal, 10) || 0;
      const paid = typeof cart.total_price === 'number' ? cart.total_price : compareTotal;
      show = codes.length > 0 || (compareTotal - paid) > 0;
    } else {
      show = codes.length > 0;
    }
    box.hidden = !show;
    if (!show) {
      if (codesWrap) codesWrap.innerHTML = '';
      // Le montant est rendu par Liquid ; ligne masquée -> on le vide.
      if (amountEl) amountEl.textContent = '';
      return;
    }

    const esc = (s) => s.replace(/[&<>"']/g, (m) => (
      { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[m]
    ));

    if (codesWrap) {
      codesWrap.innerHTML = codes
        .map((c) => {
          const safe = esc(c);
          const removable = !managed.includes(c.toLowerCase());
          return (
            '<span class="cd-code-chip">' +
            '<span class="cd-code-chip__label">' + safe + '</span>' +
            (removable
              ? '<button type="button" class="cd-code-chip__remove" data-cd-remove-code="' + safe + '" aria-label="Code entfernen">&times;</button>'
              : '') +
            '</span>'
          );
        })
        .join('');
    }

    // Montant : TOUJOURS laissé au rendu Liquid (mode séparé = cart.total_discounts,
    // mode fusionné = compare_total − total payé). L'API AJAX /cart.js ne reflète PAS
    // la remise des codes promo, donc on ne recalcule pas le montant côté JS pour ne
    // pas écraser la valeur correcte de Liquid.
  }

  /* Remove a single discount code and re-render, keeping the others. */
  removeDiscountCode(code) {
    if (!code || this._applyingDiscount) return;
    const updateUrl = (typeof routes !== 'undefined' && routes.cart_update_url) || `${this.cartRoot}cart/update.js`;

    this._applyingDiscount = true;
    fetch(`${this.cartRoot}cart.js`, { cache: 'no-store' })
      .then((r) => r.json())
      .then((cart) => {
        const keep = (cart.discount_codes || [])
          .filter((d) => d.applicable !== false)
          .map((d) => d.code)
          .filter((c) => c.toLowerCase() !== code.toLowerCase());

        return fetch(updateUrl, {
          ...fetchConfig(),
          body: JSON.stringify({
            discount: keep.join(','),
            sections: this.getSectionsToRender().map((s) => s.id).join(','),
            sections_url: window.location.pathname,
          }),
        }).then((r) => r.json());
      })
      .then((state) => {
        if (state && state.sections) this.renderContents(state, false);
        this.updateDiscountDisplay(state);
        this.renderReductions(state);
      })
      .catch((e) => console.error('cart-drawer discount remove', e))
      .finally(() => {
        this._applyingDiscount = false;
      });
  }

  /* ------------------------------------- Auto reward discount codes ------- */
  /*
    Reads tier thresholds + codes from .cd-progress data attributes and keeps
    the correct promo code applied to the cart:
      - total >= tier3 threshold -> tier3 code
      - total >= tier2 threshold -> tier2 code
      - below both               -> no managed code
    Uses /cart/update.js (discount param). Any code the customer typed manually
    (not one of the managed tier codes) is preserved. Because manual codes are
    NOT rendered by Liquid, the subtotal is patched client-side from the JSON.
  */
  get cartRoot() {
    return (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  }

  syncDiscounts() {
    if (this._syncingDiscount) return;
    const progress = this.querySelector('.cd-progress');
    if (!progress) return;

    const total = parseInt(progress.dataset.cartTotal, 10);
    if (Number.isNaN(total)) return;

    const t2 = parseInt(progress.dataset.tier2Threshold, 10);
    const t3 = parseInt(progress.dataset.tier3Threshold, 10);
    const code2 = (progress.dataset.tier2Code || '').trim();
    const code3 = (progress.dataset.tier3Code || '').trim();

    const managed = [code2, code3].filter(Boolean);
    if (managed.length === 0) return;

    let targetCode = '';
    if (code3 && t3 > 0 && total >= t3) {
      targetCode = code3;
    } else if (code2 && t2 > 0 && total >= t2) {
      targetCode = code2;
    }

    const cartJsUrl = `${this.cartRoot}cart.js`;
    const updateUrl = (typeof routes !== 'undefined' && routes.cart_update_url) || `${this.cartRoot}cart/update.js`;

    this._syncingDiscount = true;
    fetch(cartJsUrl)
      .then((r) => r.json())
      .then((cart) => {
        const applied = (cart.discount_codes || [])
          .filter((d) => d.applicable !== false)
          .map((d) => d.code);

        const appliedManaged = applied.filter((c) => managed.includes(c));
        const needsApply = targetCode && !applied.includes(targetCode);
        const needsRemove = appliedManaged.some((c) => c !== targetCode);

        if (!needsApply && !needsRemove) {
          // Nothing to change, but still make sure the displayed total reflects
          // any managed code already on the cart (Liquid won't render it).
          this.updateDiscountDisplay(cart);
          this._syncingDiscount = false;
          return null;
        }

        const keep = applied.filter((c) => !managed.includes(c));
        if (targetCode) keep.push(targetCode);

        return fetch(updateUrl, {
          ...fetchConfig(),
          body: JSON.stringify({
            discount: keep.join(','),
            sections: this.getSectionsToRender().map((s) => s.id).join(','),
            sections_url: window.location.pathname,
          }),
        })
          .then((r) => r.json())
          .then((state) => {
            if (state && state.sections) this.renderContents(state, false);
            this.updateDiscountDisplay(state);
          })
          .finally(() => {
            this._syncingDiscount = false;
          });
      })
      .catch((e) => {
        console.error('cart-drawer discount sync', e);
        this._syncingDiscount = false;
      });
  }

  /* Patch the "Total commande" row from the AJAX cart JSON (manual codes are
     absent from Liquid, so bundled section rendering can't show the reduced
     total). Struck price = prix catalogue complet (compare_total, data-attr
     Liquid, stable). Puis resynchronise les lignes Économies + Discount. */
  updateDiscountDisplay(cart) {
    if (!cart || typeof cart.total_price !== 'number') return;
    const wrap = this.querySelector('.cd-subtotal');
    if (wrap) {
      const totalEl = wrap.querySelector('.cd-subtotal__total');
      if (totalEl) {
        const ref = this.querySelector('[data-cd-savings]') || this.querySelector('[data-cd-reductions]');
        const compareTotal = ref ? (parseInt(ref.dataset.compareTotal, 10) || 0) : 0;
        const gross = compareTotal > 0
          ? compareTotal
          : (typeof cart.items_subtotal_price === 'number' ? cart.items_subtotal_price : cart.original_total_price);
        const discounted = cart.total_price < gross;

        totalEl.textContent = formatMoney(cart.total_price);

        let compareEl = wrap.querySelector('.cd-subtotal__compare');
        if (discounted) {
          if (!compareEl) {
            compareEl = document.createElement('s');
            compareEl.className = 'cd-subtotal__compare';
            totalEl.parentNode.insertBefore(compareEl, totalEl);
          }
          compareEl.textContent = formatMoney(gross);
        } else if (compareEl) {
          compareEl.remove();
        }
      }
    }

    // Garder les lignes Économies site + Discount alignées sur les mêmes données.
    this.renderReductions(cart);
  }

  /* --------------------------------------- Auto free gift (mystery product) --
     STRICT RULE — the progress bar is the single source of truth for free gifts.
     After every sync the cart may contain a gift ONLY if the bar unlocks it, and
     then exactly ONE unit of the active variant. Everything else is removed.

     A "gift line" is any cart line that is EITHER flagged with the _gift property
     (added by us) OR whose variant matches ANY configured gift product (variant A
     or B, from data-gift-variant-ids). The variant match is what catches lines
     that lost/never had the flag — e.g. the two stale "Gratis Geschenk" lines,
     a gift left over from the other variant, or one the customer added by hand.

     Rules, from a single cart.js read:
       - Keep AT MOST ONE gift line: the active variant's, only if the merchandise
         spend reaches the active threshold. Normalise its quantity to 1.
       - Remove every other gift line (orphan / duplicate / disqualified / no
         active gift at all).
       - `spend` = merchandise subtotal BEFORE cart-level discounts, minus all
         gift lines. Using total_price (after discounts) made the gift flicker:
         a % code dropped the total below the threshold right after the gift was
         added, removing and re-adding it on every price change. */
  syncGift() {
    if (this._syncingGift) return;
    const progress = this.querySelector('.cd-progress');
    if (!progress) return;

    const giftId = parseInt(progress.dataset.giftVariantId, 10) || 0;
    const giftThreshold = parseInt(progress.dataset.giftThreshold, 10) || 0;
    const giftTitle = progress.dataset.giftTitle || '';
    const knownGiftIds = (progress.dataset.giftVariantIds || '')
      .split(',')
      .map((s) => parseInt(s, 10))
      .filter((n) => n > 0);

    const isGiftLine = (i) => {
      const p = i.properties || {};
      if (p._gift === 'true' || p._gift === true) return true;
      return knownGiftIds.includes(i.variant_id);
    };

    this._syncingGift = true;
    fetch(`${this.cartRoot}cart.js`, { cache: 'no-store' })
      .then((r) => r.json())
      .then((cart) => {
        const giftLines = (cart.items || []).filter(isGiftLine);

        // Qualification is based on the merchandise total BEFORE discounts
        // (cart.original_total_price), consistent with the progress bar (Liquid
        // cart.original_total_price) and the auto discount codes. Basing it on the
        // discounted total would be circular: applying a -X% code drops the total
        // back below the threshold that unlocked it. Gift lines are subtracted so a
        // (mis)priced gift can never keep itself in the cart.
        const giftSpend = giftLines.reduce((sum, l) => sum + (l.final_line_price || 0), 0);
        const base = typeof cart.original_total_price === 'number' ? cart.original_total_price : cart.total_price;
        const spend = base - giftSpend;

        const qualifies = giftId > 0 && giftThreshold > 0 && spend >= giftThreshold;

        // Decide which single line to keep and which to zero out.
        const updates = {};
        let keep = null;
        giftLines.forEach((l) => {
          if (qualifies && l.variant_id === giftId && !keep) {
            keep = l;
            if (l.quantity !== 1) updates[l.key] = 1; // normalise quantity
          } else {
            updates[l.key] = 0; // orphan / duplicate / disqualified -> remove
          }
        });
        const needAdd = qualifies && !keep;

        if (Object.keys(updates).length === 0 && !needAdd) {
          this._syncingGift = false;
          return null; // already in sync
        }

        return this._applyGiftChanges(updates, needAdd ? giftId : 0, giftTitle);
      })
      .catch((e) => {
        console.error('cart-drawer gift sync', e);
        this._syncingGift = false;
      });
  }

  /* Apply gift line changes, then optionally add the active gift.
     Each removal/normalisation goes through /cart/change.js keyed by the line
     `key` — the canonical way to target a single line, reliable with line
     properties and duplicate variants. (/cart/update.js keys its `updates`
     object by variant id, so keying it by line key silently no-ops, which is
     why gifts were added on price-up but never removed on price-down.)
     Sections are requested only on the LAST mutation so one re-render shows the
     final state. */
  _applyGiftChanges(updates, addId, addTitle) {
    const sections = this.getSectionsToRender().map((s) => s.id).join(',');
    const sectionsUrl = window.location.pathname;
    const changes = Object.keys(updates).map((key) => [key, updates[key]]);
    const willAdd = addId > 0;

    let i = 0;
    const runNext = (prevState) => {
      if (i >= changes.length) return Promise.resolve(prevState);
      const [key, qty] = changes[i];
      i += 1;
      const isLastMutation = i >= changes.length && !willAdd;
      const body = { id: key, quantity: qty };
      if (isLastMutation) {
        body.sections = sections;
        body.sections_url = sectionsUrl;
      }
      return fetch(`${this.cartRoot}cart/change.js`, {
        ...fetchConfig(),
        body: JSON.stringify(body),
      })
        .then((r) => r.json())
        .then((state) => runNext(state));
    };

    return runNext(null)
      .then((stateAfterChanges) => {
        if (!willAdd) return stateAfterChanges;
        const properties = { _gift: 'true' };
        if (addTitle) properties._gift_title = addTitle;
        return fetch(`${this.cartRoot}cart/add.js`, {
          ...fetchConfig(),
          body: JSON.stringify({
            items: [{ id: addId, quantity: 1, properties }],
            sections,
            sections_url: sectionsUrl,
          }),
        }).then((r) => r.json());
      })
      .then((state) => {
        if (state && state.sections) this.renderContents(state, false);
        else if (state && typeof state.item_count === 'number') {
          this.classList.toggle('is-empty', state.item_count === 0);
        }
      })
      .finally(() => {
        this._syncingGift = false;
      });
  }

  /* --------------------------------------- Shipping protection (opt-out add-on) --
     Single shared implementation behind the 3 shipping_protection_a/b/c blocks
     (see snippets/cart-drawer-shipping-protection.liquid + component-cart-
     shipping-protection.css). Each block instance is a self-contained
     `[data-cd-sp]` element carrying:
       data-cd-sp-id            unique per block instance (Liquid block.id)
       data-cd-sp-variant-id    variant to add/remove
       data-cd-sp-default-on    "true"/"false" — opt-out state on first sight
       data-cd-sp-checked       current UI state, kept in sync by the methods below

     Source of truth = the cart itself: a line is "the" shipping-protection line
     for a block when its properties carry `_shipping_protection` = that block's
     id. That same flag is what cart-drawer.liquid uses to hide the line from the
     visible items list, while it still counts normally toward cart totals and
     the checkout button (both server-rendered from cart.total_price, so no
     client-side price math is needed here — only cart mutations).

     A per-block sessionStorage flag remembers an explicit customer opt-out so a
     later render (quantity change, reopening the drawer, …) never silently
     re-adds something the customer removed.

     TWO HARD INVARIANTS, enforced from a single cart.js read by `_spPlan` (the
     one place that decides what the cart should look like — both the automatic
     sync and the customer toggle go through it, so they can never disagree):

       1. NEVER ALONE — the protection product may exist only alongside at least
          one real product. Remove the last real item and every protection line
          goes with it; conversely nothing is ever added to a cart that has no
          real product. This is what stops a customer from checking out with
          "Versandschutz 1,95$" as their entire order.

       2. NEVER TWICE — at most ONE protection line per block, quantity exactly
          1. Duplicates can appear as a single line with quantity ≥ 2 (Shopify
          merges identical lines) or as several distinct lines (same variant,
          different/lost properties — e.g. a line added before the block existed,
          left over from a reconfigured product, or added by hand). Both shapes
          are detected and collapsed back to one line of one unit.

     To make invariant 2 airtight, a "protection line" is not only a line
     carrying our `_shipping_protection` flag but ALSO any line whose variant
     matches a variant managed by a block currently on the page — the same
     belt-and-braces rule the free-gift sync uses, so a line that lost its flag
     can still be recognised and cleaned up instead of lingering forever.

     Concurrency: `syncShippingProtection` (auto, on every render) and
     `toggleShippingProtection` (customer click) both mutate the cart, so they
     share ONE mutex (`_spBusy`) instead of two independent flags — running them
     concurrently was the one real race in the original version (two competing
     /cart/add.js + /cart/change.js calls could interleave and leave a phantom
     or missing line). Any call that arrives while busy is coalesced into a
     single follow-up sync (`_spPendingSync`) rather than dropped or queued
     unbounded. Every cart mutation goes through the same `_spFetchJSON` helper,
     which treats a non-2xx response or a Shopify error payload (sold-out
     variant, etc.) as a failure — the optimistic UI is reverted to the last
     confirmed state in that case, so the toggle can never get stuck showing
     "protected" when nothing was actually added. */
  syncShippingProtection() {
    this._spRun(null);
  }

  /* Customer-initiated toggle click. Flips the UI instantly (optimistic), then
     runs the exact same planner as the automatic sync with this block's desired
     state forced — so a click can never produce a cart shape the sync would
     consider invalid (protection alone, or duplicated). Reverts on failure. */
  toggleShippingProtection(block) {
    if (this._spBusy) return; // a mutation is already in flight; its own resync settles things
    const id = block.dataset.cdSpId;
    const variantId = parseInt(block.dataset.cdSpVariantId, 10) || 0;
    if (!id || variantId <= 0) {
      console.error('cart-drawer shipping-protection: block is missing a valid product/variant', block);
      return;
    }

    const currentlyChecked = block.getAttribute('data-cd-sp-checked') === 'true';
    const nextChecked = !currentlyChecked;

    this._setSPChecked(block, nextChecked);
    this._setSpDeclined(id, !nextChecked);

    this._spRun({ [id]: nextChecked }, () => {
      // Failure rollback for the block the customer actually clicked.
      this._setSPChecked(block, currentlyChecked);
      this._setSpDeclined(id, !currentlyChecked);
    });
  }

  /* Shared engine behind both entry points: read the cart once, plan, apply,
     re-render. `overrides` forces a desired state for specific block ids (the
     customer toggle); `onError` is an extra rollback for the caller's own
     optimistic UI. */
  _spRun(overrides, onError) {
    const blocks = this._spAllBlocks();
    if (blocks.length === 0) return;

    if (this._spBusy) {
      this._spPendingSync = true;
      return;
    }
    this._spBusy = true;
    this._spSetBusyUI(blocks, true);

    let states = null;
    fetch(`${this.cartRoot}cart.js`, { cache: 'no-store' })
      .then((r) => r.json())
      .then((cart) => {
        const plan = this._spPlan(cart, blocks, overrides);
        states = plan.states;
        states.forEach((s) => this._setSPChecked(s.block, s.wanted));
        return this._applySPChanges(plan.ops);
      })
      .then((state) => {
        // Ops succeeded: the cart now holds exactly the lines we wanted, so
        // record that belief. It's what lets the next sync tell "the customer
        // deleted the line" apart from "it was never added" (see _spPlan).
        if (states) states.forEach((s) => this._setSpPresent(s.id, s.wanted));
        if (state && state.sections) this.renderContents(state, false);
      })
      .catch((e) => {
        console.error('cart-drawer shipping-protection', e);
        // A failed mutation must not leave a toggle showing a state the cart
        // doesn't actually have — revert every block to what the server last
        // confirmed before this attempt.
        if (states) states.forEach((s) => this._setSPChecked(s.block, !!s.keeper));
        if (typeof onError === 'function') onError();
      })
      .finally(() => {
        this._spSetBusyUI(blocks, false);
        this._spBusy = false;
        if (this._spPendingSync) {
          this._spPendingSync = false;
          this.syncShippingProtection();
        }
      });
  }

  /* The single source of truth for what the cart should contain. Pure function
     of (cart JSON, blocks in the DOM, overrides) — no fetching, no DOM writes —
     so both invariants are decided in one readable place.
     Returns { ops, states }:
       ops    — cart mutations to run, removals/normalisations before additions
       states — per block: { block, id, variantId, wanted, keeper } where
                `keeper` is the one existing line we're keeping (or null), used
                both for the checked UI state and for the error rollback. */
  _spPlan(cart, blocks, overrides) {
    const items = Array.isArray(cart.items) ? cart.items : [];

    // Every variant currently managed by a block on the page. Used to recognise
    // protection lines that lost their `_shipping_protection` flag (invariant 2).
    const managedVariantIds = blocks
      .map((b) => parseInt(b.dataset.cdSpVariantId, 10) || 0)
      .filter((n) => n > 0);

    const isSPLine = (i) => {
      const p = i.properties || {};
      if (p._shipping_protection) return true;
      return managedVariantIds.indexOf(i.variant_id) !== -1;
    };

    const spLines = items.filter(isSPLine);
    // Invariant 1: protection only ever rides along with a real product.
    const hasRealProduct = items.some((i) => !isSPLine(i));

    const states = [];
    const keeperKeys = [];

    blocks.forEach((block) => {
      const id = block.dataset.cdSpId;
      if (!id) return; // malformed markup — nothing safe to do for this block
      const variantId = parseInt(block.dataset.cdSpVariantId, 10) || 0;
      const defaultOn = block.dataset.cdSpDefaultOn === 'true';

      // Lines that legitimately belong to THIS block: right flag AND right
      // variant. A flagged line pointing at some other variant (merchant swapped
      // the product in the editor) is intentionally excluded, so it gets cleaned
      // up below and re-added with the current variant.
      const own = spLines.filter(
        (l) => l.properties && l.properties._shipping_protection === id && l.variant_id === variantId
      );

      // The protection line is a normal, visible cart line, so the customer can
      // also delete it with the line's own trash button (never going through
      // our toggle). Detect that: we believed it was in the cart, it no longer
      // is, and there IS still a real product — that can only be a deliberate
      // removal, so honour it as an opt-out instead of instantly re-adding it.
      // When the whole cart is gone instead (checkout, "empty cart"), just drop
      // the belief without declining, so a fresh cart still gets the default.
      if (own.length === 0 && this._spPresent(id)) {
        if (hasRealProduct) this._setSpDeclined(id, true);
        this._setSpPresent(id, false);
      }

      const forced = overrides && Object.prototype.hasOwnProperty.call(overrides, id);
      let wanted = forced
        ? !!overrides[id]
        : own.length > 0 || (defaultOn && !this._spDeclined(id));
      if (!hasRealProduct || variantId <= 0) wanted = false;

      // Exactly one keeper, even if the cart somehow holds several.
      const keeper = wanted && own.length > 0 ? own[0] : null;
      if (keeper) keeperKeys.push(keeper.key);

      states.push({ block, id, variantId, wanted, keeper });
    });

    const ops = [];
    // 1. Drop every protection line that isn't a keeper: duplicates, orphans,
    //    lines from a removed/reconfigured block, and — when no real product is
    //    left — all of them.
    spLines.forEach((l) => {
      if (keeperKeys.indexOf(l.key) === -1) ops.push({ type: 'change', key: l.key, quantity: 0 });
    });
    // 2. A surviving keeper must be exactly one unit (Shopify merges identical
    //    lines, so "added twice" shows up here as quantity 2).
    states.forEach((s) => {
      if (s.keeper && s.keeper.quantity !== 1) {
        ops.push({ type: 'change', key: s.keeper.key, quantity: 1 });
      }
    });
    // 3. Finally add what's wanted but missing — always after the removals, so
    //    the cart never briefly holds two protection lines at once.
    states.forEach((s) => {
      if (s.wanted && !s.keeper) ops.push({ type: 'add', variantId: s.variantId, id: s.id });
    });

    return { ops, states };
  }

  /* Run a batch of add/remove ops sequentially (same pattern as
     _applyGiftChanges), requesting section HTML only on the last mutation so a
     single re-render shows the final state. Resolves `null` for an empty batch
     (nothing to do) and rejects — via _spFetchJSON — on the first failed op,
     leaving any remaining ops un-applied; the caller's revert + the next
     automatic sync are what bring the UI back in line, never a half-applied
     silent state. */
  _applySPChanges(ops) {
    if (!ops || ops.length === 0) return Promise.resolve(null);
    const sections = this.getSectionsToRender().map((s) => s.id).join(',');
    const sectionsUrl = window.location.pathname;

    let i = 0;
    const runNext = (prevState) => {
      if (i >= ops.length) return Promise.resolve(prevState);
      const op = ops[i];
      i += 1;
      const isLast = i >= ops.length;
      return this._spRunOp(op, isLast, sections, sectionsUrl).then((state) => runNext(state));
    };
    return runNext(null);
  }

  _spRunOp(op, isLast, sections, sectionsUrl) {
    if (op.type === 'change') {
      // quantity 0 removes the line; quantity 1 normalises a duplicated one.
      const body = { id: op.key, quantity: op.quantity };
      if (isLast) {
        body.sections = sections;
        body.sections_url = sectionsUrl;
      }
      return this._spFetchJSON(`${this.cartRoot}cart/change.js`, body);
    }
    const body = { items: [{ id: op.variantId, quantity: 1, properties: { _shipping_protection: op.id } }] };
    if (isLast) {
      body.sections = sections;
      body.sections_url = sectionsUrl;
    }
    return this._spFetchJSON(`${this.cartRoot}cart/add.js`, body);
  }

  /* Thin wrapper around fetch() used by every shipping-protection cart
     mutation: treats a non-2xx response, or a 200 response carrying a Shopify
     error payload (e.g. sold-out variant on /cart/add.js), as a failure
     instead of silently returning a body without `.sections` — the one gap
     that let the earlier version leave a stale "checked" toggle after a
     failed add. */
  _spFetchJSON(url, body) {
    return fetch(url, { ...fetchConfig(), body: JSON.stringify(body) }).then((response) =>
      response
        .json()
        .catch(() => ({}))
        .then((json) => {
          const shopifyError = json && (json.errors || (typeof json.status === 'number' && json.status >= 400));
          if (!response.ok || shopifyError) {
            const detail = (json && (json.description || json.message || json.errors)) || `HTTP ${response.status}`;
            throw new Error(typeof detail === 'string' ? detail : JSON.stringify(detail));
          }
          return json;
        })
    );
  }

  _spAllBlocks() {
    return Array.from(this.querySelectorAll('[data-cd-sp]'));
  }

  /* Disables every toggle-ish control (checkbox toggle + optional opt-out
     link, both real <button>s) while a mutation is in flight. Native
     `disabled` blocks both mouse and keyboard activation, so this is a second,
     browser-level guard on top of the `_spBusy` JS mutex — a rapid double
     click/tap can never queue two overlapping mutations for the same or a
     different block. */
  _spSetBusyUI(blocks, busy) {
    blocks.forEach((block) => {
      if (!block) return;
      block.classList.toggle('is-busy', busy);
      block.querySelectorAll('[data-cd-sp-toggle]').forEach((el) => {
        el.disabled = busy;
      });
    });
  }

  /* Reflects `checked` on the block wrapper's data attribute + every toggle
     control's aria-pressed state (style B has two: the checkbox toggle and
     the opt-out link) — every visual (checkmark fill, swapped title, opt-out
     link visibility) is driven from the single [data-cd-sp-checked] attribute
     via CSS, so this is the only place the UI state is written. */
  _setSPChecked(block, checked) {
    block.setAttribute('data-cd-sp-checked', checked ? 'true' : 'false');
    block.querySelectorAll('[data-cd-sp-toggle]').forEach((el) => {
      el.setAttribute('aria-pressed', checked ? 'true' : 'false');
    });
  }

  _spDeclinedKey(id) {
    return `cd-sp-declined-${id}`;
  }

  _spDeclined(id) {
    try {
      return sessionStorage.getItem(this._spDeclinedKey(id)) === '1';
    } catch (e) {
      return false; // sessionStorage unavailable (e.g. private mode) — fall back to default_on
    }
  }

  _setSpDeclined(id, declined) {
    try {
      if (declined) sessionStorage.setItem(this._spDeclinedKey(id), '1');
      else sessionStorage.removeItem(this._spDeclinedKey(id));
    } catch (e) {
      /* ignore — nothing to persist to, next render falls back to default_on */
    }
  }

  /* "We last left a protection line in the cart for this block." Compared with
     what the cart actually holds to detect a removal made outside our toggle
     (the line's own trash button) — see _spPlan. Without storage the check
     simply never fires and the block falls back to its default_on behaviour. */
  _spPresentKey(id) {
    return `cd-sp-present-${id}`;
  }

  _spPresent(id) {
    try {
      return sessionStorage.getItem(this._spPresentKey(id)) === '1';
    } catch (e) {
      return false;
    }
  }

  _setSpPresent(id, present) {
    try {
      if (present) sessionStorage.setItem(this._spPresentKey(id), '1');
      else sessionStorage.removeItem(this._spPresentKey(id));
    } catch (e) {
      /* ignore */
    }
  }
}

/* Minimal money formatter (ported from Shopify.formatMoney) so the drawer can
   render totals from JSON without depending on the cart-page script. */
function formatMoney(cents, format) {
  const fmt = format || window.moneyFormat || '${{amount}}';
  if (typeof cents === 'string') cents = cents.replace('.', '');
  const placeholder = /\{\{\s*(\w+)\s*\}\}/;

  const number = (value, precision, thousands, decimal) => {
    precision = precision == null ? 2 : precision;
    thousands = thousands == null ? ',' : thousands;
    decimal = decimal == null ? '.' : decimal;
    if (isNaN(value) || value == null) return '0';
    value = (value / 100).toFixed(precision);
    const parts = value.split('.');
    const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    const centsPart = parts[1] ? decimal + parts[1] : '';
    return dollars + centsPart;
  };

  let value = '';
  const match = fmt.match(placeholder);
  switch (match && match[1]) {
    case 'amount':
      value = number(cents, 2);
      break;
    case 'amount_no_decimals':
      value = number(cents, 0);
      break;
    case 'amount_with_comma_separator':
      value = number(cents, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = number(cents, 0, '.', ',');
      break;
    case 'amount_with_space_separator':
      value = number(cents, 2, ' ', ',');
      break;
    case 'amount_no_decimals_with_space_separator':
      value = number(cents, 0, ' ', '');
      break;
    default:
      value = number(cents, 2);
  }
  return fmt.replace(placeholder, value);
}

if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', CartDrawer);

/* Size-guide open/close inside the cart drawer.
   Two guide types can appear in the upsell cards:
     - guidedetaille (plain size table) — its own inline script is omitted in the
       drawer (see guidedetaille.liquid), so we drive open/close here.
     - ss-modal-size-v2 (interactive calculator) — driven by its own re-run inline
       script; here we only mirror the open/close state.
   Both are visually trapped by the drawer (.drawer__inner has a transform +
   overflow:hidden, which makes position:fixed relative to the drawer and clips
   it). We toggle `cd-guide-open` on <cart-drawer> so the CSS can neutralise the
   transform and let the modal cover the whole viewport.
   The `cart-drawer` ancestor check keeps product-page instances untouched. */
if (!window.__cdGuideBound) {
  window.__cdGuideBound = true;

  const setGuideOpen = (node, open) => {
    const drawer = node.closest('cart-drawer');
    if (drawer) drawer.classList.toggle('cd-guide-open', open);
    return drawer;
  };

  // Lift a guide modal out of .drawer__inner (which has a transform +
  // overflow:hidden that trap and clip fixed children) up to the <cart-drawer>
  // root (fixed, no transform, no overflow). This makes the guide cover the
  // whole viewport WITHOUT shifting the size-picker (.cd-size-modal) behind it,
  // which was the jump seen on opening. A comment marks its home for restore.
  const liftOut = (el, drawer) => {
    if (!el || !drawer || el.__cdHome) return;
    const home = document.createComment('cd-guide-home');
    el.parentNode.insertBefore(home, el);
    el.__cdHome = home;
    drawer.appendChild(el);
  };
  const restoreEl = (el) => {
    const home = el && el.__cdHome;
    if (home && home.parentNode) home.parentNode.insertBefore(el, home);
    if (home) home.remove();
    if (el) el.__cdHome = null;
  };

  document.addEventListener('click', (e) => {
    // guidedetaille — open
    const opener = e.target.closest('#tableau-guidetaille .show-modall');
    if (opener && opener.closest('cart-drawer')) {
      e.preventDefault();
      const section = opener.closest('#tableau-guidetaille');
      const drawer = setGuideOpen(opener, true);
      section.querySelector('.mask-modall')?.classList.add('active');
      section.querySelector('.modall')?.classList.add('modall-active');
      liftOut(section, drawer);
      return;
    }
    // guidedetaille — close
    const closer = e.target.closest('#tableau-guidetaille .close-modall, #tableau-guidetaille .mask-modall');
    if (closer && closer.closest('cart-drawer')) {
      e.preventDefault();
      const section = closer.closest('#tableau-guidetaille');
      section.querySelector('.mask-modall')?.classList.remove('active');
      section.querySelector('.modall')?.classList.remove('modall-active');
      restoreEl(section);
      setGuideOpen(closer, false);
      return;
    }
    // ss-modal-size-v2 — open (its own inline script toggles display; we lift + flag)
    const ssOpen = e.target.closest('.show-sizeModal');
    if (ssOpen && ssOpen.closest('cart-drawer')) {
      const drawer = setGuideOpen(ssOpen, true);
      const modal = ssOpen.closest('.cd-size-modal__head')?.querySelector('.modal')
        || document.getElementById((ssOpen.id || '').replace('open_modal_btn-', 'size_modal-'));
      liftOut(modal, drawer);
      return;
    }
    // ss-modal-size-v2 — close (close button or backdrop click on .modal)
    const ssModal = (e.target.closest('.modal .close') && e.target.closest('.modal'))
      || (e.target.classList && e.target.classList.contains('modal') ? e.target : null);
    if (ssModal && ssModal.closest('cart-drawer')) {
      restoreEl(ssModal);
      setGuideOpen(e.target, false);
    }
  });

  // Escape closes any open guide and restores the DOM.
  document.addEventListener('keyup', (e) => {
    if (e.key !== 'Escape') return;
    document.querySelectorAll('cart-drawer.cd-guide-open').forEach((drawer) => {
      drawer.querySelectorAll('#tableau-guidetaille').forEach((section) => {
        section.querySelector('.mask-modall')?.classList.remove('active');
        section.querySelector('.modall')?.classList.remove('modall-active');
        restoreEl(section);
      });
      drawer.querySelectorAll('.modal').forEach((modal) => {
        modal.style.display = 'none';
        restoreEl(modal);
      });
      drawer.classList.remove('cd-guide-open');
    });
  });
}

// Defined inside a function so a missing CartItems (cart.js not loaded yet)
// never throws at top level and never aborts the rest of this file.
function defineCartDrawerItems() {
  if (customElements.get('cart-drawer-items')) return;
  if (typeof CartItems === 'undefined') {
    setTimeout(defineCartDrawerItems, 50);
    return;
  }
  class CartDrawerItems extends CartItems {
    constructor() {
      super();
      this.addEventListener('click', this.onQuantityButtonClick.bind(this), true);
    }

    onQuantityButtonClick(event) {
      const button = event.target.closest('.cd-qty__btn');
      if (!button) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      const qtyInput = button.closest('quantity-input')?.querySelector('.cd-qty__input');
      if (!qtyInput) return;

      const lineItem = button.closest('.cd-item');
      const line = lineItem?.id?.replace('CartDrawer-Item-', '') || qtyInput.dataset.index;
      if (!line) return;

      const variantId = qtyInput.dataset.quantityVariantId;
      let quantity = parseInt(qtyInput.value, 10) || 0;

      if (button.name === 'plus') {
        quantity += 1;
      } else if (button.name === 'minus') {
        quantity = Math.max(0, quantity - 1);
      } else {
        return;
      }

      qtyInput.value = quantity;
      this.updateQuantity(line, quantity, button.name, variantId);
    }

    getSectionsToRender() {
      return [
        { id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' },
        { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
      ];
    }
  }
  customElements.define('cart-drawer-items', CartDrawerItems);
}
defineCartDrawerItems();

/* -------------------------------------------------- Scrolling announcement */
class CartAnnouncement extends HTMLElement {
  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('.cd-announce__slide'));
    if (this.slides.length <= 1) return;

    this.index = Math.max(0, this.slides.findIndex((s) => s.classList.contains('is-active')));
    const speed = (parseFloat(this.dataset.speed) || 4) * 1000;

    this.querySelectorAll('.cd-announce__arrow').forEach((btn) => {
      btn.addEventListener('click', () => {
        this._stop();
        this._go(btn.dataset.dir === 'next' ? 1 : -1);
      });
    });

    this.addEventListener('mouseenter', () => this._stop());
    this.addEventListener('mouseleave', () => this._start(speed));
    this._start(speed);
  }

  disconnectedCallback() {
    this._stop();
  }

  _start(speed) {
    this._stop();
    this.timer = setInterval(() => this._go(1), speed);
  }

  _stop() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  _go(dir) {
    this.slides[this.index].classList.remove('is-active');
    this.index = (this.index + dir + this.slides.length) % this.slides.length;
    this.slides[this.index].classList.add('is-active');
  }
}

if (!customElements.get('cd-announcement')) customElements.define('cd-announcement', CartAnnouncement);

/* --------------------------------------------------- Upsell quick-add card */
/*
  Carte upsell avec modale de sélection de taille (copie Honeylove) :
    - swatches couleur (option 1) filtrent la variante
    - PICK SIZE ouvre la modale centrée (une ligne de boutons par option, sans guide)
    - sélection de taille -> résout la variante -> ADD TO BAG
    - ADD TO BAG -> soumet le <product-form> (AJAX via product-form.js, rouvre le drawer)
*/
class CartUpsellCard extends HTMLElement {
  connectedCallback() {
    this.idInput = this.querySelector('.cd-up-card__variant-id');
    this.submitBtn = this.querySelector('.cd-up-card__submit');
    this.image = this.querySelector('.cd-up-card__image');
    this.modal = this.querySelector('.cd-size-modal');
    this.modalAdd = this.querySelector('[data-cd-modal-add]');
    this.modalImage = this.querySelector('.cd-size-modal__image');

    // Parse variant data
    const dataEl = this.querySelector('.cd-variant-data');
    try {
      this.data = dataEl ? JSON.parse(dataEl.textContent) : { variants: [], options: [] };
    } catch (e) {
      this.data = { variants: [], options: [] };
    }

    // Current selection state (option value per position)
    this.selection = { 1: null, 2: null, 3: null };

    // Default color = first swatch
    const firstSwatch = this.querySelector('.cd-swatch.is-active') || this.querySelector('.cd-swatch');
    if (firstSwatch) this.selection[1] = firstSwatch.dataset.color;

    // Swatch clicks (color = option 1)
    this.querySelectorAll('.cd-swatch').forEach((sw) => {
      sw.addEventListener('click', () => this._selectColor(sw));
    });

    // Toute la carte ouvre la modale de choix de taille (zone cliquable élargie).
    // On ignore les clics à l'intérieur de la modale (ses propres contrôles) et
    // le bouton d'ajout direct (produit sans taille) qui garde son comportement.
    this.addEventListener('click', (e) => {
      if (this.modal && this.modal.contains(e.target)) return;
      if (e.target.closest('[data-cd-quickadd]')) return;
      if (this.modal) this._openModal();
    });

    // Direct quick-add (no variants)
    const quick = this.querySelector('[data-cd-quickadd]');
    if (quick) quick.addEventListener('click', () => this._submit());

    // Modal option buttons
    this.querySelectorAll('.cd-size-opt').forEach((btn) => {
      btn.addEventListener('click', () => this._selectOption(btn));
    });

    // Modal close
    this.querySelectorAll('[data-cd-close-modal]').forEach((el) => {
      el.addEventListener('click', () => this._closeModal());
    });

    // Modal ADD TO BAG
    if (this.modalAdd) this.modalAdd.addEventListener('click', () => this._submit());

    this._refreshOptionAvailability();
  }

  _selectColor(sw) {
    this.querySelectorAll('.cd-swatch').forEach((s) => s.classList.remove('is-active'));
    sw.classList.add('is-active');
    this.selection[1] = sw.dataset.color;

    // Update card + modal image from a matching variant image
    const variant = this.data.variants.find((v) => v.o1 === this.selection[1] && v.img);
    if (variant && variant.img) {
      if (this.image) this.image.src = variant.img;
      if (this.modalImage) this.modalImage.src = variant.img;
    }
    this._refreshOptionAvailability();
    this._resolveVariant();
  }

  _openModal() {
    if (!this.modal) return;
    this.modal.hidden = false;
    document.body.classList.add('cd-modal-open');
  }

  _closeModal() {
    if (!this.modal) return;
    this.modal.hidden = true;
    document.body.classList.remove('cd-modal-open');
  }

  _selectOption(btn) {
    if (btn.disabled) return;
    const pos = btn.dataset.optPosition;
    // Deselect siblings in the same option group
    this.querySelectorAll(`.cd-size-opt[data-opt-position="${pos}"]`).forEach((b) =>
      b.classList.remove('is-selected')
    );
    btn.classList.add('is-selected');
    this.selection[pos] = btn.dataset.value;
    this._resolveVariant();
  }

  /* Disable option values that have no available variant with the chosen color */
  _refreshOptionAvailability() {
    this.querySelectorAll('.cd-size-opt').forEach((btn) => {
      const pos = parseInt(btn.dataset.optPosition, 10);
      const value = btn.dataset.value;
      const ok = this.data.variants.some((v) => {
        if (!v.available) return false;
        if (this.selection[1] && v.o1 !== this.selection[1]) return false;
        return v['o' + pos] === value;
      });
      btn.disabled = !ok;
    });
  }

  /* Match a full variant from the current selection; enable ADD TO BAG */
  _resolveVariant() {
    const needed = this.data.options.map((o) => o.position);
    const match = this.data.variants.find((v) =>
      needed.every((pos) => {
        // color (pos 1) taken from swatch selection; others from modal
        const sel = this.selection[pos];
        return sel != null && v['o' + pos] === sel;
      })
    );

    if (match && match.available) {
      if (this.idInput) this.idInput.value = match.id;
      if (match.img) {
        if (this.image) this.image.src = match.img;
        if (this.modalImage) this.modalImage.src = match.img;
      }
      if (this.modalAdd) this.modalAdd.disabled = false;
      this._resolved = match.id;
    } else {
      if (this.modalAdd) this.modalAdd.disabled = true;
      this._resolved = null;
    }
  }

  _submit() {
    if (this.submitBtn) {
      this._closeModal();
      this.submitBtn.click();
    }
  }
}

if (!customElements.get('cd-upsell-card')) customElements.define('cd-upsell-card', CartUpsellCard);
